import { Activity, Baby, Droplet, Frown, HeartPulse, Ruler, Scale, Syringe, Thermometer, Weight, Wind, type LucideIcon } from 'lucide-react';
import { vitalFlag, type VitalFlag } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import type { Encounter, EncounterVitals } from './types';

export interface VitalReading {
  key: string;
  label: string;
  icon: LucideIcon;
  tone: string;
  value: string | null;
  unit: string;
  flag: VitalFlag | null;
}

export const VITAL_STYLE = {
  bp: { icon: Activity, tone: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' },
  pulse: { icon: HeartPulse, tone: 'bg-pink-500/10 text-pink-600 dark:text-pink-400' },
  temperature: { icon: Thermometer, tone: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  spo2: { icon: Droplet, tone: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  respiratoryRate: { icon: Wind, tone: 'bg-teal-500/10 text-teal-600 dark:text-teal-400' },
  weight: { icon: Weight, tone: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  height: { icon: Ruler, tone: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  bmi: { icon: Scale, tone: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  sugar: { icon: Syringe, tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  pain: { icon: Frown, tone: 'bg-slate-500/10 text-slate-600 dark:text-slate-300' },
  headCircumference: { icon: Baby, tone: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' },
} satisfies Record<string, { icon: LucideIcon; tone: string }>;

export const PAIN_SCORE_HIGH = 7;

export function painFlag(score: number | null | undefined): VitalFlag | null {
  return score !== null && score !== undefined && score >= PAIN_SCORE_HIGH ? 'high' : null;
}

function worst(...flags: (VitalFlag | null)[]): VitalFlag | null {
  return flags.find((flag) => flag !== null) ?? null;
}

function shown(value: number | null | undefined, digits = 0): string | null {
  if (value === null || value === undefined) return null;
  return digits > 0 ? value.toFixed(digits).replace(/\.0+$/, '') : String(value);
}

export function vitalReadings(vitals: EncounterVitals | undefined, options: { all?: boolean } = {}): VitalReading[] {
  const v = vitals;
  const readings: VitalReading[] = [
    {
      key: 'bp',
      label: tr('opd', 'vital_bp'),
      ...VITAL_STYLE.bp,
      value: v?.bpSystolic ? `${v.bpSystolic}/${v.bpDiastolic ?? '–'}` : null,
      unit: 'mmHg',
      flag: worst(vitalFlag('bpSystolic', v?.bpSystolic), vitalFlag('bpDiastolic', v?.bpDiastolic)),
    },
    {
      key: 'pulse',
      label: tr('opd', 'vital_pulse'),
      ...VITAL_STYLE.pulse,
      value: shown(v?.pulse),
      unit: '/min',
      flag: vitalFlag('pulse', v?.pulse),
    },
    {
      key: 'temperature',
      label: tr('opd', 'vital_temperature'),
      ...VITAL_STYLE.temperature,
      value: shown(v?.temperatureC, 1),
      unit: '°C',
      flag: vitalFlag('temperatureC', v?.temperatureC),
    },
    {
      key: 'spo2',
      label: tr('opd', 'vital_spo2'),
      ...VITAL_STYLE.spo2,
      value: shown(v?.spo2),
      unit: '%',
      flag: vitalFlag('spo2', v?.spo2),
    },
    {
      key: 'respiratoryRate',
      label: tr('opd', 'vital_resp_rate'),
      ...VITAL_STYLE.respiratoryRate,
      value: shown(v?.respiratoryRate),
      unit: '/min',
      flag: vitalFlag('respiratoryRate', v?.respiratoryRate),
    },
    {
      key: 'weight',
      label: tr('opd', 'vital_weight'),
      ...VITAL_STYLE.weight,
      value: shown(v?.weightKg, 1),
      unit: 'kg',
      flag: null,
    },
    {
      key: 'height',
      label: tr('opd', 'vital_height'),
      ...VITAL_STYLE.height,
      value: shown(v?.heightCm, 1),
      unit: 'cm',
      flag: null,
    },
    {
      key: 'bmi',
      label: tr('opd', 'vital_bmi'),
      ...VITAL_STYLE.bmi,
      value: shown(v?.bmi, 1),
      unit: '',
      flag: null,
    },
    {
      key: 'sugar',
      label: tr('opd', 'vital_sugar'),
      ...VITAL_STYLE.sugar,
      value: shown(v?.bloodSugarMgDl),
      unit: 'mg/dL',
      flag: vitalFlag('bloodSugarMgDl', v?.bloodSugarMgDl),
    },
    {
      key: 'pain',
      label: tr('opd', 'vital_pain'),
      ...VITAL_STYLE.pain,
      value: v?.painScore === null || v?.painScore === undefined ? null : `${v.painScore}/10`,
      unit: '',
      flag: painFlag(v?.painScore),
    },
  ];
  const core = new Set(['bp', 'pulse', 'temperature', 'spo2', 'weight']);
  return options.all ? readings : readings.filter((reading) => core.has(reading.key) || reading.value !== null);
}

export function nextVitalsAreRecheck(encounter: Pick<Encounter, 'context' | 'vitals'>): boolean {
  return encounter.context === 'opd' && encounter.vitals.length > 0;
}
